import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Search, FileText, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import StatCard from "@/components/dashboard/StatCard";
import UrgentBanner from "@/components/dashboard/UrgentBanner";
import ActivityFeed from "@/components/dashboard/ActivityFeed";
import DebtorRowCard from "@/components/dashboard/DebtorRowCard";
import TierChangeModal from "@/components/jea/TierChangeModal";
import RunQueryModal from "@/components/queries/RunQueryModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { shortDate } from "@/lib/dates";
import Skeleton from "@/components/jea/Skeleton";
import { usePreviewGate } from "@/hooks/usePreviewGate";

/**
 * Standard Dashboard.
 * Shows urgent banner (if any), 4 stat cards, debtor cards (left, 65%),
 * Recent Activity panel (right, 320px fixed).
 */
const StandardDashboard = ({ user }) => {
  const nav = useNavigate();
  const { inPreview, guardAction } = usePreviewGate();
  const [debtors, setDebtors] = useState([]);
  const [slotUsed, setSlotUsed] = useState(0);
  const [slotMax, setSlotMax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierModal, setTierModal] = useState({ open: false, debtor: null, submitting: false });
  const [urgent, setUrgent] = useState(null);
  const [runQuery, setRunQuery] = useState({ open: false, debtor: null });
  const [stats, setStats] = useState({ runs_this_year: 0, reports_total: 0 });

  const loadStats = async () => {
    try {
      const { data } = await apiClient.get("/queries/stats");
      setStats(data);
    } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/debtors");
      const active = (data.debtors || []).filter((d) => d.status !== "archived");
      setDebtors(active);
      setSlotUsed(data.slot_used ?? active.length);
      setSlotMax(data.slot_max ?? null);
      // Compute urgent: fetch critical dates for each; take earliest ≤30d judgment_expires
      const urgentList = [];
      await Promise.all(active.map(async (d) => {
        try {
          const { data: cd } = await apiClient.get(`/debtors/${d.id}/critical-dates`);
          const je = cd?.dates?.judgment_expires?.days_until;
          if (je != null && je >= 0 && je <= 30) {
            urgentList.push({ debtor_id: d.id, name: d.full_name, days_until: je, reason: "judgment_expires" });
          }
        } catch { /* ignore */ }
      }));
      urgentList.sort((a, b) => a.days_until - b.days_until);
      if (urgentList.length > 0) {
        setUrgent({ ...urgentList[0], total_urgent: urgentList.length });
      } else {
        setUrgent(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadStats(); }, []);

  const nextScheduledQuery = useMemo(() => {
    const dates = debtors.map((d) => d.next_query_at).filter(Boolean).sort();
    return dates[0] || null;
  }, [debtors]);

  const openTier = (d) => setTierModal({ open: true, debtor: d, submitting: false });
  const confirmTier = async (newTier) => {
    if (!tierModal.debtor) return;
    setTierModal((t) => ({ ...t, submitting: true }));
    try {
      await apiClient.patch(`/debtors/${tierModal.debtor.id}`, { tier: newTier });
      toast.success("Tier updated — next query date recomputed.");
      setTierModal({ open: false, debtor: null, submitting: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setTierModal((t) => ({ ...t, submitting: false }));
    }
  };

  const atCap = slotMax != null && slotUsed >= slotMax;
  const handleAddDebtor = guardAction(
    () => {
      if (atCap) {
        toast.info("You've hit your Standard judgment cap — opening PRO upgrade.");
        nav("/billing?open=change-plan&target=pro-monthly");
        return;
      }
      nav("/debtors/new");
    },
    { feature: "adding judgments" }
  );
  const openRunQuery = (d) => setRunQuery({ open: true, debtor: d });
  const closeRunQuery = () => setRunQuery({ open: false, debtor: null });
  const onQueryDone = (result) => {
    closeRunQuery();
    loadStats();
    if (result?.query_run?.id && result?.query_run?.debtor_id) {
      nav(`/debtors/${result.query_run.debtor_id}/queries/${result.query_run.id}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="standard-dashboard">
      <div className="space-y-1">
        <div className="jea-caption">Home</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">
          Welcome, <span className="text-accent-pink">{user?.full_name || "there"}</span>
        </h1>
      </div>

      {urgent && <UrgentBanner urgent={urgent} />}

      <div className="grid grid-cols-4 gap-4" data-testid="standard-stats">
        <StatCard label="Active Judgments" value={debtors.length} Icon={Users} loading={loading} testid="stat-active-debtors" />
        <StatCard label="Queries Run This Year" value={stats.runs_this_year} Icon={Search} loading={loading} testid="stat-queries-year" />
        <StatCard label="Reports Available" value={stats.reports_total} Icon={FileText} loading={loading} testid="stat-reports" />
        <StatCard
          label="Next Scheduled Query"
          value={nextScheduledQuery ? shortDate(nextScheduledQuery) : "—"}
          valueClass="!text-[16px] jea-mono !text-text-secondary"
          Icon={Calendar}
          loading={loading}
          testid="stat-next-query"
        />
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-6" data-testid="standard-content">
        <div className="min-w-0 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold text-[18px]">My Judgments</h2>
            <JeaButton
              onClick={handleAddDebtor}
              disabled={atCap && !inPreview}
              className="gap-2"
              data-testid="dashboard-add-debtor"
            >
              <Plus size={16} /> Add New Judgment
            </JeaButton>
          </div>

          {loading ? (
            <div className="space-y-3" data-testid="dashboard-debtors-skeleton">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="bg-bg-secondary border border-border-default rounded-[12px] p-6 flex items-center gap-6">
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                    <div className="grid grid-cols-3 gap-4">
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                      <Skeleton className="h-8" />
                    </div>
                  </div>
                  <div className="w-24 space-y-2">
                    <Skeleton className="h-9" />
                    <Skeleton className="h-9" />
                  </div>
                </div>
              ))}
            </div>
          ) : debtors.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No judgments yet"
              description="Add your first judgment to start tracking enforcement."
              testid="dashboard-debtors-empty"
              footer={
                <JeaButton className="gap-2" onClick={handleAddDebtor} data-testid="dashboard-empty-add">
                  <Plus size={16} /> Add Your First Judgment
                </JeaButton>
              }
            />
          ) : (
            <div className="space-y-3">
              {debtors.slice(0, 3).map((d) => (
                <DebtorRowCard
                  key={d.id}
                  debtor={d}
                  onOpen={() => nav(`/debtors/${d.id}`)}
                  onRunQuery={() => openRunQuery(d)}
                  onViewReports={() => nav(`/debtors/${d.id}?tab=reports`)}
                  onEditTier={() => openTier(d)}
                />
              ))}
            </div>
          )}
        </div>

        <ActivityFeed />
      </div>

      <TierChangeModal
        open={tierModal.open}
        currentTier={tierModal.debtor?.tier}
        submitting={tierModal.submitting}
        onCancel={() => setTierModal({ open: false, debtor: null, submitting: false })}
        onConfirm={confirmTier}
      />

      <RunQueryModal
        open={runQuery.open}
        debtor={runQuery.debtor}
        onClose={closeRunQuery}
        onCompleted={onQueryDone}
      />
    </div>
  );
};

export default StandardDashboard;
